import React, { useEffect, useMemo } from "react";
import { isEmpty } from "lodash";
import { computed, observable } from "mobx";
import { Observer } from "mobx-react";
import { Paper } from "@material-ui/core";
import { BookingSyncReport_Get } from "@graphql";
import { ErrorCard, SpinnerOverlay } from "@components";
import SearchPageLayout from "@search/components/SearchPageLayout";
import SearchOptions from "@search/SearchOptions";
import { BookingSearchForm } from "../BookingSearchForm";
import { BookingFilterManager } from "../BookingFilterManager";
import { BookingSearchActionBar } from "../BookingSearchActionBar";
import { BookingFilterGroup, groupBy } from "../utils";
import { Empty } from "./Empty";
import { BookingSyncReportTable } from "./BookingSyncReportTable";
import { Label } from "@common/typography/Label";
import locale from "@bonliva-crm/locale";
import { usePolling } from "@bonliva-crm/ui";

type Props = {
  onOpen: (id: string, tab?: string) => void;
  onEdit: (id?: string) => void;
  onGroupBy: (groupBy: BookingFilterGroup) => void;
};

class BookingSyncReportStore {
  @observable data = null as BookingSyncReport_Get.Return["bookingSyncReport"];
  @observable isLoading = false;
  @observable error = "";
  filterManager = BookingFilterManager.Create({});

  constructor() {
    this.filterManager.fetchEmitter.on(this.fetch);
    setTimeout(() => this.fetch(), 200);
    this.filterManager.unhideFields();
  }

  unsubscribe = () => this.filterManager.fetchEmitter.off(this.fetch);

  @computed get showSelectUsers() {
    return isEmpty(this.filterManager.syncReportQueryInput.projectResponsiblePersonId);
  }

  @computed get showKitSupport() {
    return true;
  }

  fetch = () => {
    if (isEmpty(this.filterManager.filter.projectResponsiblePersonId)) {
      this.data = null;
      this.error = "";
      return Promise.resolve();
    }

    this.isLoading = true;
    this.error = "";

    return BookingSyncReport_Get.func(
      { input: this.filterManager.syncReportQueryInput },
      { fetchPolicy: "network-only" }
    )
      .then(({ data: _data }) => {
        this.data = _data.bookingSyncReport;
      })
      .catch((e) => {
        this.error = e.toString();
      })
      .finally(() => {
        this.isLoading = false;
      });
  };
}

export function BookingSyncReport(props: Props) {
  const store = useMemo(() => new BookingSyncReportStore(), []);

  useEffect(() => {
    return () => {
      store.unsubscribe();
    };
  }, [store]);

  usePolling(
    async () => {
      await store.fetch();
    },
    "/bookings",
    { interval: 5000, refetchOnPushState: true }
  );

  return (
    <SearchPageLayout
      wrapContent={(content) => <Paper>{content}</Paper>}
      actionBar={
        <>
          <BookingSearchActionBar
            onDirectBooking={() => props.onEdit(null)}
            total={null}
          />

          <SearchOptions
            activeGroupBy={BookingFilterGroup.Sync}
            groupBy={groupBy()}
            onGroupBy={(v) => props.onGroupBy(v.value)}
          />
        </>
      }
      content={
        <Observer>
          {() => (
            <div style={{ position: "relative", minHeight: 500 }}>
              <SpinnerOverlay transparent show={store.isLoading && !store.data} />
              {store.error && <ErrorCard message={store.error} title="error" />}
              {store.data && <BookingSyncReportTable data={store.data} />}
              {store.showSelectUsers && <Empty />}
              {store.data?.Total === 0 && !store.showSelectUsers && (
                <div style={{ textAlign: "center" }}>
                  <Label>{locale.NoSearchResults}</Label>
                </div>
              )}
            </div>
          )}
        </Observer>
      }
      filter={
        <div>
          <BookingSearchForm
            manager={store.filterManager}
            group={BookingFilterGroup.Sync}
          />
        </div>
      }
    />
  );
}
